/**
 * How the check of the rows added to a quantity composition is asked: in batches the endpoint
 * accepts, of the portfolio when there is one, and otherwise — a composition being created, or a
 * draft — at today in the composition's currency.
 */
import type { Currencies, NewInstrument, ResolvedInstrument } from "@eu.mdotm.api/sphere";
import { newInstrumentChecksRepository } from "./repository";

/** The most instruments one request may carry. */
export const RESOLVE_BATCH_SIZE = 50;

/**
 * Checks `instruments`, one engine run per instrument. More than {@link RESOLVE_BATCH_SIZE} go out as
 * consecutive requests, and the answers come back concatenated in request order; one batch failing
 * fails the whole check. With neither a portfolio nor a currency it rejects before asking.
 */
export async function resolveInstruments(params: {
	investmentUuid?: string;
	instruments: NewInstrument[];
	referenceDate: string;
	currency?: Currencies;
}): Promise<ResolvedInstrument[]> {
	const { investmentUuid, referenceDate, currency } = params;
	const ask = (instruments: NewInstrument[]) => {
		if (investmentUuid != null) {
			return newInstrumentChecksRepository.resolveNewInstruments(investmentUuid, { instruments, referenceDate });
		}
		if (currency == null) {
			throw new Error("Checking instruments for a composition that belongs to no portfolio needs its currency");
		}
		return newInstrumentChecksRepository.resolveNewInstrumentsOfNewPortfolio({ instruments, currency });
	};
	const answers: ResolvedInstrument[] = [];
	for (let start = 0; start < params.instruments.length; start += RESOLVE_BATCH_SIZE) {
		answers.push(...(await ask(params.instruments.slice(start, start + RESOLVE_BATCH_SIZE))));
	}
	return answers;
}
